import { useEffect, useState } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Download, FileSpreadsheet, Star, Clock } from 'lucide-react';
import * as XLSX from 'xlsx';

export default function Shippers({ user }) {
  const [shippers, setShippers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [formData, setFormData] = useState({
    legal_name: '', dba: '', contact_name: '', title: '', phone: '', email: '',
    billing_address: '', shipping_locations: '', commodity: '',
    avg_weekly_loads: 0, pickup_hours: '', delivery_requirements: '',
    accessorials: '', notes: '', grade: 'A', payout_days: 30
  });

  const verifyBusiness = async () => {
    if (!formData.legal_name) {
      toast.error('Please enter business name first');
      return;
    }

    setVerifyLoading(true);
    try {
      // Extract state from billing address if possible
      const addressParts = formData.billing_address.split(',');
      const state = addressParts.length > 1 ? addressParts[addressParts.length - 2].trim() : '';

      const response = await api.post('/business/verify', {
        business_name: formData.legal_name,
        state: state
      });

      const data = response.data;
      
      // Open verification services in new tabs
      toast.success('Opening business verification services...');
      
      // Open BBB
      window.open(data.verification_services[0].url, '_blank');
      
      // Show toast with recommendations
      setTimeout(() => {
        toast.info('Verify: BBB ratings, Secretary of State registration, D&B profile, and request W-9 for EIN verification', {
          duration: 8000
        });
      }, 1000);

    } catch (err) {
      toast.error('Verification failed. Try manual lookup.');
    } finally {
      setVerifyLoading(false);
    }
  };

  useEffect(() => {
    loadShippers();
  }, []);

  const loadShippers = () => {
    api.get('/shippers')
      .then(res => setShippers(res.data))
      .catch(() => toast.error('Failed to load brokers'))
      .finally(() => setLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    api.post('/shippers', formData)
      .then(() => {
        toast.success('Broker added successfully');
        setShowForm(false);
        loadShippers();
        resetForm();
      })
      .catch(() => toast.error('Failed to add broker'));
  };

  const resetForm = () => {
    setFormData({
      legal_name: '', dba: '', contact_name: '', title: '', phone: '', email: '',
      billing_address: '', shipping_locations: '', commodity: '',
      avg_weekly_loads: 0, pickup_hours: '', delivery_requirements: '',
      accessorials: '', notes: '', grade: 'A', payout_days: 30
    });
  };

  const exportToExcel = () => {
    api.get('/export/shippers', { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'brokers.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Excel exported');
      })
      .catch(() => toast.error('Failed to export'));
  };

  const viewSpreadsheet = () => {
    const ws = XLSX.utils.json_to_sheet(shippers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Brokers');
    const htmlStr = XLSX.utils.sheet_to_html(ws);
    const newWindow = window.open('');
    newWindow.document.write(`
      <html>
        <head><title>Brokers Spreadsheet</title>
        <style>
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #4a5568; color: white; }
        </style>
        </head>
        <body>${htmlStr}</body>
      </html>
    `);
  };

  return (
    <div className="space-y-6" data-testid="shippers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Brokers</h1>
          <p className="text-slate-600 mt-1">Manage your broker database</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={viewSpreadsheet} variant="outline" data-testid="view-spreadsheet-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            View Spreadsheet
          </Button>
          <Button onClick={exportToExcel} variant="outline" data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild>
              <Button data-testid="add-shipper-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Broker
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Broker</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm font-semibold text-green-900 mb-2">✓ Business Verification</p>
                  <p className="text-xs text-green-700 mb-3">Enter business name and address below, then click "Verify Business" to check BBB ratings, Secretary of State registration, D&B credit profile, and business legitimacy.</p>
                  <Button 
                    type="button" 
                    onClick={verifyBusiness} 
                    disabled={verifyLoading}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="verify-business-btn"
                  >
                    {verifyLoading ? 'Verifying...' : '✓ Verify Business & Credit'}
                  </Button>
                  <p className="text-xs text-green-600 mt-2">Recommended: Also request W-9 form and check 3 trade references</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Legal Name *</Label>
                    <Input required value={formData.legal_name} onChange={e => setFormData({...formData, legal_name: e.target.value})} placeholder="ABC Manufacturing Inc" />
                  </div>
                  <div>
                    <Label>DBA</Label>
                    <Input value={formData.dba} onChange={e => setFormData({...formData, dba: e.target.value})} placeholder="ABC Products" />
                  </div>
                  <div>
                    <Label>Contact Name *</Label>
                    <Input required value={formData.contact_name} onChange={e => setFormData({...formData, contact_name: e.target.value})} />
                  </div>
                  <div>
                    <Label>Title *</Label>
                    <Input required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input required type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input required type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                  </div>
                  <div className="col-span-2">
                    <Label>Billing Address *</Label>
                    <Input required value={formData.billing_address} onChange={e => setFormData({...formData, billing_address: e.target.value})} />
                  </div>
                  <div className="col-span-2">
                    <Label>Shipping Locations *</Label>
                    <Input required value={formData.shipping_locations} onChange={e => setFormData({...formData, shipping_locations: e.target.value})} />
                  </div>
                  <div>
                    <Label>Commodity *</Label>
                    <Input required value={formData.commodity} onChange={e => setFormData({...formData, commodity: e.target.value})} />
                  </div>
                  <div>
                    <Label>Avg Weekly Loads *</Label>
                    <Input required type="number" value={formData.avg_weekly_loads} onChange={e => setFormData({...formData, avg_weekly_loads: parseInt(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Pickup Hours *</Label>
                    <Input required value={formData.pickup_hours} onChange={e => setFormData({...formData, pickup_hours: e.target.value})} />
                  </div>
                  <div>
                    <Label>Delivery Requirements *</Label>
                    <Input required value={formData.delivery_requirements} onChange={e => setFormData({...formData, delivery_requirements: e.target.value})} />
                  </div>
                  <div>
                    <Label>Broker Grade</Label>
                    <Select value={formData.grade} onValueChange={v => setFormData({...formData, grade: v})}>
                      <SelectTrigger data-testid="grade-select">
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A - Excellent</SelectItem>
                        <SelectItem value="B">B - Good</SelectItem>
                        <SelectItem value="C">C - Average</SelectItem>
                        <SelectItem value="D">D - Below Average</SelectItem>
                        <SelectItem value="F">F - Poor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Payout Days</Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={formData.payout_days} 
                      onChange={e => setFormData({...formData, payout_days: parseInt(e.target.value) || 0})} 
                      placeholder="30"
                    />
                    <p className="text-xs text-slate-500 mt-1">Days until payment</p>
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Input value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
                  </div>
                </div>
                <Button type="submit" className="w-full">Add Broker</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shippers.map(shipper => {
          const gradeColors = {
            'A': 'bg-green-100 text-green-800 border-green-300',
            'B': 'bg-blue-100 text-blue-800 border-blue-300',
            'C': 'bg-yellow-100 text-yellow-800 border-yellow-300',
            'D': 'bg-orange-100 text-orange-800 border-orange-300',
            'F': 'bg-red-100 text-red-800 border-red-300'
          };
          const grade = shipper.grade || 'A';
          const payoutDays = shipper.payout_days || 30;
          
          return (
          <Card key={shipper.id} className="card-hover" data-testid={`shipper-card-${shipper.id}`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{shipper.legal_name}</CardTitle>
                  <p className="text-sm text-slate-500">Commodity: {shipper.commodity}</p>
                  {shipper.created_by_pin && <p className="text-xs text-blue-600 font-semibold">Agent: {shipper.created_by_pin}</p>}
                </div>
                <div className={`px-3 py-1 rounded-full border font-bold text-lg ${gradeColors[grade]}`}>
                  {grade}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-4 p-2 bg-slate-50 rounded-lg mb-2">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium">Grade: {grade}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium">Net {payoutDays} Days</span>
                </div>
              </div>
              <p className="text-sm"><strong>Contact:</strong> {shipper.contact_name}</p>
              <p className="text-sm"><strong>Phone:</strong> {shipper.phone}</p>
              <p className="text-sm"><strong>Email:</strong> {shipper.email}</p>
              <p className="text-sm"><strong>Avg Weekly Loads:</strong> {shipper.avg_weekly_loads}</p>
              {user.role === 'super_admin' && (
                <div className="pt-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-red-600 hover:text-red-700 w-full"
                    onClick={() => {
                      if (window.confirm('Delete this broker? Only master admin can delete.')) {
                        api.delete(`/shippers/${shipper.id}`)
                          .then(() => {
                            toast.success('Broker deleted');
                            loadShippers();
                          })
                          .catch(err => toast.error(err.response?.data?.detail || 'Failed to delete'));
                      }
                    }}
                    data-testid={`delete-${shipper.id}`}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          );
        })}
      </div>
    </div>
  );
}
